import {
  apiClient,
  getDemoNumbersOccupancyEndpoint,
  maxDailyDistinctUsersPerDemoNumber,
  superAdminEmail,
} from "../../constants.ts";
import { toast } from "react-hot-toast";
import { transact, tx, useAuth, useQuery } from "./db.ts";
import { usePageMeta } from "./pageMeta.ts";
import { PageSpinner } from "./components.tsx";
import { useEffect, useState } from "preact/hooks";

interface DemoNumberRow {
  id: string;
  number: string;
  blocked: boolean;
  kind: string;
  createdAt: number;
  secretToken?: string;
  reservedForPayingUsers?: boolean;
}

const formatPhone = (phone: string) =>
  phone.startsWith("972")
    ? `+972 ${phone.slice(3, 5)}-${phone.slice(5, 8)}-${phone.slice(8)}`
    : `+${phone}`;

const kindLabel = (kind: string) =>
  kind === "business"
    ? "WhatsApp for Business"
    : "Supergreen (WhatsApp Client)";

const kindCanAddGroups = (kind: string) => kind !== "business";

const kindOrder = (kind: string) => (kind === "business" ? 1 : 0);

const sortNumbers = (rows: DemoNumberRow[]) =>
  rows.toSorted((a, b) =>
    kindOrder(a.kind) - kindOrder(b.kind) || a.createdAt - b.createdAt
  );

const toggleBlocked = (entry: DemoNumberRow) => () =>
  toast.promise(
    transact(tx.demoNumbers[entry.id].update({ blocked: !entry.blocked })),
    {
      loading: "Updating status...",
      success: `${formatPhone(entry.number)} marked ${
        entry.blocked ? "Connected" : "Blocked"
      }`,
      error: "Failed to update status",
    },
  );

const toggleReserved = (entry: DemoNumberRow) => () =>
  toast.promise(
    transact(
      tx.demoNumbers[entry.id].update({
        reservedForPayingUsers: !entry.reservedForPayingUsers,
      }),
    ),
    {
      loading: "Updating reservation...",
      success: `${formatPhone(entry.number)} marked ${
        entry.reservedForPayingUsers ? "Public" : "Paying Users Only"
      }`,
      error: "Failed to update reservation",
    },
  );

const ReservedBadge = ({ reserved }: { reserved: boolean }) =>
  reserved
    ? (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-50 dark:bg-indigo-950/30 text-indigo-700 dark:text-indigo-300 border border-indigo-200 dark:border-indigo-900/30">
        <span className="h-1.5 w-1.5 rounded-full bg-indigo-600 dark:bg-indigo-400">
        </span>
        Paying Users Only
      </span>
    )
    : (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-50 dark:bg-gray-950/30 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-900/30">
        <span className="h-1.5 w-1.5 rounded-full bg-gray-600 dark:bg-gray-400">
        </span>
        Public
      </span>
    );

const ReservedCell = (
  { entry, canEdit }: { entry: DemoNumberRow; canEdit: boolean },
) =>
  canEdit
    ? (
      <button
        type="button"
        onClick={toggleReserved(entry)}
        title="Click to toggle reservation"
        className="cursor-pointer rounded-full transition-opacity hover:opacity-70 focus:outline-none focus:ring-2 focus:ring-indigo-400"
      >
        <ReservedBadge reserved={!!entry.reservedForPayingUsers} />
      </button>
    )
    : <ReservedBadge reserved={!!entry.reservedForPayingUsers} />;

const StatusBadge = ({ blocked }: { blocked: boolean }) =>
  blocked
    ? (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-50 dark:bg-red-950/30 text-red-700 dark:text-red-300 border border-red-200 dark:border-red-900/30">
        <span className="h-1.5 w-1.5 rounded-full bg-red-600 dark:bg-red-400">
        </span>
        Blocked
      </span>
    )
    : (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium bg-emerald-50 dark:bg-emerald-950/30 text-emerald-700 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-900/30">
        <span className="h-1.5 w-1.5 rounded-full bg-emerald-600 dark:bg-emerald-400">
        </span>
        Connected
      </span>
    );

const StatusCell = (
  { entry, canEdit }: { entry: DemoNumberRow; canEdit: boolean },
) =>
  canEdit
    ? (
      <button
        type="button"
        onClick={toggleBlocked(entry)}
        title="Click to toggle status"
        className="cursor-pointer rounded-full transition-opacity hover:opacity-70 focus:outline-none focus:ring-2 focus:ring-emerald-400"
      >
        <StatusBadge blocked={entry.blocked} />
      </button>
    )
    : <StatusBadge blocked={entry.blocked} />;

const cleanPhone = (phone: string) => phone.replace(/\D/g, "");

const occupancyBarWidth = (current: number, max: number) =>
  `${Math.min(100, Math.round((current / Math.max(max, 1)) * 100))}%`;

const OccupancyBadge = ({ current, max }: { current: number; max: number }) => {
  const isFull = current >= max;
  const hasUsers = current > 0;
  const badgeClass = isFull
    ? "bg-rose-50 dark:bg-rose-950/30 text-rose-700 dark:text-rose-300 border-rose-200 dark:border-rose-900/30"
    : hasUsers
    ? "bg-amber-50 dark:bg-amber-950/30 text-amber-700 dark:text-amber-300 border-amber-200 dark:border-amber-900/30"
    : "bg-emerald-50 dark:bg-emerald-950/30 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-900/30";

  const dotClass = isFull
    ? "bg-rose-600 dark:bg-rose-400"
    : hasUsers
    ? "bg-amber-600 dark:bg-amber-400"
    : "bg-emerald-600 dark:bg-emerald-400";

  const barClass = isFull
    ? "bg-rose-500 dark:bg-rose-400"
    : hasUsers
    ? "bg-amber-500 dark:bg-amber-400"
    : "bg-emerald-500 dark:bg-emerald-400";

  return (
    <div
      className="flex flex-col gap-1.5"
      title={`Daily limit: ${max} users per number (resets at 00:00 UTC). Currently ${current} of ${max} slots occupied.`}
    >
      <div className="flex items-center gap-1.5">
        <span
          className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium border ${badgeClass}`}
        >
          <span className={`h-1.5 w-1.5 rounded-full ${dotClass}`}></span>
          <span>{current} / {max} {isFull ? "Full" : "Users"}</span>
        </span>
      </div>
      <div className="w-20 bg-gray-200 dark:bg-neutral-800 rounded-full h-1 overflow-hidden">
        <div
          className={`h-1 rounded-full transition-all duration-300 ${barClass}`}
          style={{ width: occupancyBarWidth(current, max) }}
        >
        </div>
      </div>
    </div>
  );
};

const OccupancyCell = ({
  stats,
  loading,
}: {
  stats?: { current: number; max: number };
  loading: boolean;
}) =>
  loading && !stats
    ? (
      <span className="text-xs text-text-tertiary-light dark:text-text-tertiary">
        Loading...
      </span>
    )
    : (
      <OccupancyBadge
        current={stats?.current ?? 0}
        max={stats?.max ?? maxDailyDistinctUsersPerDemoNumber}
      />
    );

const TokenCell = ({
  entry,
  tokens,
  canEdit,
}: {
  entry: DemoNumberRow;
  tokens: {
    id: string;
    number: string;
    secretToken?: string;
    businessToken?: string;
    businessWabaId?: string;
    businessAppSecret?: string;
    businessVerifyToken?: string;
  }[];
  canEdit: boolean;
}) => {
  if (!canEdit) return null;
  const match = tokens.find((t) => t.number === entry.number);

  const updateField = (field: string, label: string) => () => {
    const currentVal = match
      ? (match as Record<string, string | undefined>)[field] || ""
      : "";
    const newVal = prompt(`Enter ${label} for this demo number:`, currentVal);
    if (newVal !== null) {
      if (match) {
        toast.promise(
          transact(
            tx.demoNumberTokens[match.id].update({ [field]: newVal }),
          ),
          {
            loading: `Updating ${label}...`,
            success: `${label} updated`,
            error: `Failed to update ${label}`,
          },
        );
      } else {
        toast.promise(
          transact(
            tx.demoNumberTokens[crypto.randomUUID()].create({
              number: entry.number,
              [field]: newVal,
            }),
          ),
          {
            loading: `Creating ${label}...`,
            success: `${label} created`,
            error: `Failed to create ${label}`,
          },
        );
      }
    }
  };

  if (entry.kind === "business") {
    const wabaToken = match?.businessToken || "";
    const wabaId = match?.businessWabaId || "";
    const appSecret = match?.businessAppSecret || "";
    const verifyToken = match?.businessVerifyToken || "";

    return (
      <td className="py-4 px-4 font-mono text-xs">
        <div className="flex flex-col gap-1 align-start text-left">
          <button
            type="button"
            onClick={updateField("businessToken", "Business Token")}
            className="text-text-secondary-light dark:text-text-secondary hover:text-emerald-500 dark:hover:text-emerald-400 underline font-mono text-left"
          >
            Token: {wabaToken ? `${wabaToken.slice(0, 8)}...` : "Set"}
          </button>
          <button
            type="button"
            onClick={updateField("businessWabaId", "WABA ID")}
            className="text-text-secondary-light dark:text-text-secondary hover:text-emerald-500 dark:hover:text-emerald-400 underline font-mono text-left"
          >
            WABA ID: {wabaId || "Set"}
          </button>
          <button
            type="button"
            onClick={updateField("businessAppSecret", "App Secret")}
            className="text-text-secondary-light dark:text-text-secondary hover:text-emerald-500 dark:hover:text-emerald-400 underline font-mono text-left"
          >
            Secret: {appSecret ? `${appSecret.slice(0, 8)}...` : "Set"}
          </button>
          <button
            type="button"
            onClick={updateField("businessVerifyToken", "Verify Token")}
            className="text-text-secondary-light dark:text-text-secondary hover:text-emerald-500 dark:hover:text-emerald-400 underline font-mono text-left"
          >
            Verify: {verifyToken || "Set"}
          </button>
        </div>
      </td>
    );
  }

  // Supergreen / Whatsapp Client demo number:
  const token = match?.secretToken || "";
  return (
    <td className="py-4 px-4 font-mono text-xs">
      <button
        type="button"
        onClick={updateField("secretToken", "Secret Token")}
        className="text-text-secondary-light dark:text-text-secondary hover:text-emerald-500 dark:hover:text-emerald-400 underline font-mono"
      >
        {token ? `${token.slice(0, 8)}...` : "Set token"}
      </button>
    </td>
  );
};

export const DemoNumbersPage = () => {
  usePageMeta({
    title: "WhatsApp Demo Numbers | prompt2bot",
    description:
      "Check the status and availability of our shared WhatsApp demo numbers.",
  });

  const { user } = useAuth();
  const token = user?.refresh_token;
  const isSuperAdmin = user?.email === superAdminEmail;

  const [occupancy, setOccupancy] = useState<
    Record<string, { current: number; max: number }>
  >({});
  const [loadingOccupancy, setLoadingOccupancy] = useState(true);

  useEffect(() => {
    if (!token) return;
    apiClient({
      token,
      endpoint: getDemoNumbersOccupancyEndpoint,
      payload: {},
    })
      .then((res) => {
        if (res.occupancy) {
          setOccupancy(res.occupancy);
        }
      })
      .catch((err) => {
        console.error("Failed to load demo numbers occupancy:", err);
      })
      .finally(() => {
        setLoadingOccupancy(false);
      });
  }, [token]);

  const [showAddForm, setShowAddForm] = useState(false);
  const [newNumber, setNewNumber] = useState("");
  const [newKind, setNewKind] = useState<"supergreen" | "business">(
    "supergreen",
  );
  const [newToken, setNewToken] = useState("");
  const [newReserved, setNewReserved] = useState(false);

  const query = {
    demoNumbers: {},
    ...(isSuperAdmin && { demoNumberTokens: {} }),
  };
  const { data, isLoading, error } = useQuery(query);

  if (isLoading) {
    return <PageSpinner />;
  }

  const allNumbers = sortNumbers((data?.demoNumbers ?? []) as DemoNumberRow[]);
  const tokens = (data?.demoNumberTokens ?? []) as {
    id: string;
    number: string;
    secretToken: string;
  }[];

  return (
    <div className="max-w-4xl mx-auto my-10 p-6 rounded-xl shadow-md bg-surface-raised-light dark:bg-surface-raised border border-border-subtle-light dark:border-border-subtle text-text-primary-light dark:text-text-primary">
      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-2">WhatsApp Demo Numbers</h1>
        <p className="text-sm text-text-secondary-light dark:text-text-secondary">
          View the real-time status and capability of our shared WhatsApp demo
          numbers.
        </p>
      </div>

      {error
        ? (
          <div className="p-4 mb-6 rounded-lg bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-900/50 text-red-800 dark:text-red-200 text-sm">
            {error.message}
          </div>
        )
        : (
          <>
            <div className="mb-8 p-5 border border-emerald-200 bg-emerald-50/40 dark:border-emerald-950/40 dark:bg-emerald-950/20 rounded-xl">
              <h2 className="font-semibold text-emerald-950 dark:text-emerald-50 text-base mb-2 flex items-center gap-2">
                <span className="flex h-2 w-2 relative">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75">
                  </span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500">
                  </span>
                </span>
                How to Test Your Agent
              </h2>
              <p className="text-xs text-emerald-900/80 dark:text-emerald-100/80 leading-relaxed mb-4">
                To route the shared demo number to your custom agent, simply
                send the switch command returned in your bot config. You can add
                any connected Supergreen number to any group chat and send the
                same switch command inside the group to enable the bot there!
              </p>
              <div className="text-xs bg-emerald-100/50 dark:bg-emerald-950/50 border border-emerald-200/50 dark:border-emerald-900/50 px-3 py-2 rounded font-mono text-emerald-800 dark:text-emerald-300 w-fit">
                /switch &lt;your_bot_code&gt;
              </div>
            </div>

            {isSuperAdmin && (
              <div className="mb-8 p-5 border border-emerald-200/50 bg-emerald-50/10 dark:border-emerald-950/40 dark:bg-emerald-950/5 rounded-xl">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-emerald-950 dark:text-emerald-50 text-sm">
                    Admin Tools: Add Demo Number
                  </h3>
                  <button
                    type="button"
                    onClick={() => setShowAddForm(!showAddForm)}
                    className="text-xs font-semibold px-3 py-1.5 rounded-lg border border-emerald-600 bg-emerald-600 text-white hover:bg-emerald-700 transition-colors"
                  >
                    {showAddForm ? "Hide form" : "Add Number"}
                  </button>
                </div>

                {showAddForm && (
                  <form
                    onSubmit={(e) => {
                      e.preventDefault();
                      if (!newNumber) {
                        toast.error("Please enter a phone number");
                        return;
                      }
                      const numberClean = newNumber.replace(/\D/g, "");
                      const demoId = crypto.randomUUID();
                      const txs = [
                        tx.demoNumbers[demoId].create({
                          number: numberClean,
                          blocked: false,
                          kind: newKind,
                          createdAt: Date.now(),
                          reservedForPayingUsers: newReserved,
                        }),
                      ];
                      if (newKind === "supergreen" && newToken) {
                        (txs as unknown as unknown[]).push(
                          tx.demoNumberTokens[crypto.randomUUID()].create({
                            number: numberClean,
                            secretToken: newToken,
                          }),
                        );
                      }
                      toast.promise(
                        transact(
                          txs as unknown as Parameters<typeof transact>[0],
                        ),
                        {
                          loading: "Adding demo number...",
                          success: "Demo number added!",
                          error: "Failed to add demo number",
                        },
                      ).then(() => {
                        setNewNumber("");
                        setNewToken("");
                        setNewReserved(false);
                        setShowAddForm(false);
                      });
                    }}
                    className="space-y-4 max-w-md animate-fadeIn"
                  >
                    <div className="space-y-1">
                      <label className="text-xs font-semibold text-text-secondary-light dark:text-text-secondary">
                        Phone Number (digits only, e.g. 972536549671)
                      </label>
                      <input
                        type="text"
                        value={newNumber}
                        onInput={(e) =>
                          setNewNumber((e.target as HTMLInputElement).value)}
                        placeholder="972536549671"
                        className="w-full px-3 py-2 text-sm rounded-lg border border-border-subtle bg-surface border-border-subtle-light dark:border-border-subtle"
                      />
                    </div>

                    <div className="space-y-1">
                      <label className="text-xs font-semibold text-text-secondary-light dark:text-text-secondary">
                        Type
                      </label>
                      <select
                        value={newKind}
                        onChange={(e) =>
                          setNewKind(
                            (e.target as HTMLSelectElement).value as
                              | "supergreen"
                              | "business",
                          )}
                        className="w-full px-3 py-2 text-sm rounded-lg border bg-surface border-border-subtle-light dark:border-border-subtle"
                      >
                        <option value="supergreen">
                          Supergreen (WhatsApp Client)
                        </option>
                        <option value="business">
                          WhatsApp for Business (Official)
                        </option>
                      </select>
                    </div>

                    {newKind === "supergreen" && (
                      <div className="space-y-1">
                        <label className="text-xs font-semibold text-text-secondary-light dark:text-text-secondary">
                          Secret Token (Optional)
                        </label>
                        <input
                          type="text"
                          value={newToken}
                          onInput={(e) =>
                            setNewToken((e.target as HTMLInputElement).value)}
                          placeholder="Supergreen client secret token"
                          className="w-full px-3 py-2 text-sm rounded-lg border bg-surface border-border-subtle-light dark:border-border-subtle"
                        />
                      </div>
                    )}

                    <div className="flex items-center gap-2 py-1">
                      <input
                        type="checkbox"
                        id="newReserved"
                        checked={newReserved}
                        onChange={(e) =>
                          setNewReserved(
                            (e.target as HTMLInputElement).checked,
                          )}
                        className="h-4 w-4 rounded border-border-subtle bg-surface border-border-subtle-light dark:border-border-subtle text-emerald-600 focus:ring-emerald-500"
                      />
                      <label
                        htmlFor="newReserved"
                        className="text-xs font-semibold text-text-secondary-light dark:text-text-secondary cursor-pointer"
                      >
                        Reserved for Paying Users
                      </label>
                    </div>

                    <button
                      type="submit"
                      className="w-full py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm font-semibold transition-colors"
                    >
                      Save Demo Number
                    </button>
                  </form>
                )}

                <div className="mt-4 pt-4 border-t border-emerald-200/20 text-xs leading-relaxed space-y-2 text-text-secondary-light dark:text-text-secondary">
                  <p className="font-semibold text-emerald-950 dark:text-emerald-50">
                    Webhook Configuration Instructions:
                  </p>
                  <ul className="list-disc pl-4 space-y-1 font-mono">
                    <li>
                      <strong>Supergreen WhatsApp webhook:</strong> Configure on
                      {" "}
                      <a
                        href="https://supergreen.cc"
                        target="_blank"
                        rel="noreferrer"
                        className="underline"
                      >
                        supergreen.cc
                      </a>{" "}
                      to point to: <br />
                      <span className="text-emerald-600 dark:text-emerald-400 font-bold bg-surface px-1.5 py-0.5 rounded">
                        https://api.prompt2bot.com/whatsapp-demo
                      </span>
                    </li>
                    <li>
                      <strong>WhatsApp for Business webhook:</strong>{" "}
                      Configure in Meta Developer App dashboard to point to:
                      {" "}
                      <br />
                      <span className="text-emerald-600 dark:text-emerald-400 font-bold bg-surface px-1.5 py-0.5 rounded">
                        https://api.prompt2bot.com/whatsapp-demo-business
                      </span>
                    </li>
                  </ul>
                </div>
              </div>
            )}

            {isSuperAdmin && (
              <div className="mb-6 text-xs text-text-tertiary-light dark:text-text-tertiary">
                You are signed in as the super admin. Click a status badge to
                toggle whether a number is blocked, or the access badge to
                toggle reservation.
              </div>
            )}

            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="border-b border-border-subtle-light dark:border-border-subtle text-xs font-semibold uppercase tracking-wider text-text-tertiary-light dark:text-text-tertiary">
                    <th className="py-3 px-4">Phone Number</th>
                    <th className="py-3 px-4">Type</th>
                    <th className="py-3 px-4">Access</th>
                    <th className="py-3 px-4">Today's Occupancy</th>
                    <th className="py-3 px-4">Can Be Added to Groups</th>
                    {isSuperAdmin && (
                      <th className="py-3 px-4">
                        Secret Token
                      </th>
                    )}
                    <th className="py-3 px-4 text-right">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border-subtle-light dark:divide-border-subtle text-sm">
                  {allNumbers.map((entry) => (
                    <tr
                      key={entry.number}
                      className="hover:bg-surface-hover-light dark:hover:bg-surface-hover transition-colors"
                    >
                      <td className="py-4 px-4 font-mono font-medium">
                        <a
                          href={`https://wa.me/${entry.number}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-emerald-600 dark:text-emerald-400 hover:underline inline-flex items-center gap-1 font-semibold"
                        >
                          {formatPhone(entry.number)}
                          <svg
                            className="w-3.5 h-3.5 opacity-60 group-hover:opacity-100 transition-opacity"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth={2}
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                            />
                          </svg>
                        </a>
                      </td>
                      <td className="py-4 px-4 text-xs font-medium text-text-secondary-light dark:text-text-secondary">
                        {kindLabel(entry.kind)}
                      </td>
                      <td className="py-4 px-4">
                        <ReservedCell entry={entry} canEdit={isSuperAdmin} />
                      </td>
                      <td className="py-4 px-4">
                        <OccupancyCell
                          stats={occupancy[entry.number] ??
                            occupancy[cleanPhone(entry.number)]}
                          loading={loadingOccupancy}
                        />
                      </td>
                      <td className="py-4 px-4">
                        {kindCanAddGroups(entry.kind)
                          ? (
                            <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-50 dark:bg-green-950/30 text-green-700 dark:text-green-300 border border-green-200 dark:border-green-900/30">
                              Yes
                            </span>
                          )
                          : (
                            <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium bg-neutral-50 dark:bg-neutral-900/30 text-neutral-600 dark:text-neutral-400 border border-neutral-200 dark:border-neutral-900/30">
                              No
                            </span>
                          )}
                      </td>
                      {isSuperAdmin && (
                        <TokenCell
                          entry={entry}
                          tokens={tokens}
                          canEdit={isSuperAdmin}
                        />
                      )}
                      <td className="py-4 px-4 text-right">
                        <StatusCell entry={entry} canEdit={isSuperAdmin} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="mt-3 text-xs text-text-tertiary-light dark:text-text-tertiary">
              Shared demo numbers are limited to{" "}
              {maxDailyDistinctUsersPerDemoNumber}{" "}
              distinct users per day (resets daily at 00:00 UTC) to ensure
              reliability.
            </div>

            <div className="mt-8 p-5 border border-amber-200 bg-amber-50/40 dark:border-amber-950/40 dark:bg-amber-950/20 rounded-xl flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
              <div className="space-y-1">
                <h3 className="font-semibold text-amber-950 dark:text-amber-50 text-sm">
                  Tired of chasing blocked numbers?
                </h3>
                <p className="text-xs text-amber-900/80 dark:text-amber-100/80 leading-relaxed max-w-2xl">
                  Get your own stable, dedicated WhatsApp number! Use{" "}
                  <a
                    href="https://supergreen.cc"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline font-semibold hover:text-amber-700 dark:hover:text-amber-300"
                  >
                    supergreen.cc
                  </a>{" "}
                  or configure an official WhatsApp Business integration (just
                  ask the AI Builder Bot to set it up for you).
                </p>
              </div>
            </div>
          </>
        )}
    </div>
  );
};
